import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DrawerFooter,
	Input,
	Label,
	Separator,
	Textarea,
} from "@monbits/ui";
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import * as React from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AMLDrawerContent } from "src/components/aml-drawer-content";
import { EntityDrawer } from "src/components/entity-drawer";
import {
	useAMLTransactionDetails,
	useApproveAMLTransaction,
	useRecheckAMLTransaction,
	useRejectAMLTransaction,
	useSkipAMLTransaction,
} from "src/queries/aml-queries";
import type { AMLTransactionsOutletContext } from "./aml-transactions-list";

type DecisionType = "approve" | "reject" | "skip" | "recheck" | null;

export function AMLTransactionDetail() {
	const { transactionId } = useParams<{ transactionId: string }>();
	const [activeDialog, setActiveDialog] = React.useState<DecisionType>(null);
	const [decisionReason, setDecisionReason] = React.useState("");
	const [totpCode, setTotpCode] = React.useState("");

	const outletContext = useOutletContext<
		AMLTransactionsOutletContext | undefined
	>();
	const listTransactions = outletContext?.transactions ?? [];
	const isListLoading = outletContext?.isLoading ?? false;
	const approveMutation = useApproveAMLTransaction();
	const rejectMutation = useRejectAMLTransaction();
	const skipMutation = useSkipAMLTransaction();
	const recheckMutation = useRecheckAMLTransaction();

	// Parse transaction ID
	const parsedId = React.useMemo(() => {
		if (!transactionId) return null;
		const id = Number.parseInt(transactionId, 10);
		return Number.isNaN(id) ? null : id;
	}, [transactionId]);

	// Get transaction from list for initial display
	const listTransaction = React.useMemo(() => {
		if (!parsedId) return null;
		return listTransactions.find((item) => item.id === parsedId) ?? null;
	}, [listTransactions, parsedId]);

	// Fetch full transaction details with metadata
	const detailsQuery = useAMLTransactionDetails(parsedId);

	// Use detailed data if available, fall back to list data
	const transaction = detailsQuery.data?.transaction ?? listTransaction;
	const amlCheck = detailsQuery.data?.amlCheck ?? transaction?.amlCheck ?? null;
	const isLoading = isListLoading && !listTransaction;
	const isLoadingMetadata = detailsQuery.isLoading;

	const isOnHold =
		transaction?.status === "on_hold" || transaction?.status === "onHold";
	const hasError = Boolean(amlCheck?.errorMessage);

	const closeDialog = () => {
		setActiveDialog(null);
		setDecisionReason("");
		setTotpCode("");
	};

	const handleDecision = async () => {
		if (!transaction || !activeDialog) {
			return;
		}

		const trimmedReason = decisionReason.trim();
		if (!trimmedReason) {
			toast.error("Please provide a reason");
			return;
		}

		const requires2FA = activeDialog !== "recheck";
		if (requires2FA && totpCode.trim().length !== 6) {
			toast.error("Invalid 2FA code", {
				description: "Please enter a 6-digit code",
			});
			return;
		}

		try {
			if (activeDialog === "approve") {
				await approveMutation.mutateAsync({
					id: transaction.id,
					data: { reason: trimmedReason, totpCode: totpCode.trim() },
				});
				toast.success("Transaction approved", {
					description:
						"The transaction has been approved and will be processed",
				});
			} else if (activeDialog === "reject") {
				await rejectMutation.mutateAsync({
					id: transaction.id,
					data: { reason: trimmedReason, totpCode: totpCode.trim() },
				});
				toast.success("Transaction rejected", {
					description: "The transaction has been rejected",
				});
			} else if (activeDialog === "skip") {
				await skipMutation.mutateAsync({
					id: transaction.id,
					data: { reason: trimmedReason, totpCode: totpCode.trim() },
				});
				toast.success("AML check skipped", {
					description: "The transaction will proceed without AML verification",
				});
			} else {
				await recheckMutation.mutateAsync({
					id: transaction.id,
					data: { reason: trimmedReason },
				});
				toast.success("AML recheck triggered", {
					description: "A new AML check has been initiated",
				});
			}

			closeDialog();
		} catch (error: unknown) {
			console.error("Failed to process decision", error);
			const errorMessage =
				(error as { response?: { data?: { message?: string } } })?.response
					?.data?.message || "Failed to process transaction";
			toast.error("Action failed", {
				description: errorMessage,
			});
		}
	};

	const drawerContent = (
		<>
			<div className="flex-1 overflow-y-auto">
				{transaction ? (
					<AMLDrawerContent
						transaction={transaction}
						amlCheck={amlCheck}
						isLoadingMetadata={isLoadingMetadata}
					/>
				) : null}
			</div>

			<Separator />

			<DrawerFooter className="flex-shrink-0 p-4 space-y-2">
				<div className="flex gap-2">
					<Button
						variant="outline"
						className="flex-1"
						onClick={() => {
							setActiveDialog("reject");
							setDecisionReason("");
							setTotpCode("");
						}}
						disabled={!isOnHold}
					>
						Reject
					</Button>
					<Button
						className="flex-1"
						onClick={() => {
							setActiveDialog("approve");
							setDecisionReason("");
							setTotpCode("");
						}}
						disabled={!isOnHold}
					>
						Approve
					</Button>
				</div>
				{hasError && (
					<Button
						variant="secondary"
						onClick={() => {
							setActiveDialog("skip");
							setDecisionReason("");
							setTotpCode("");
						}}
						disabled={!isOnHold}
					>
						Skip AML Check
					</Button>
				)}
				<Button
					variant="ghost"
					onClick={() => {
						setActiveDialog("recheck");
						setDecisionReason("");
						setTotpCode("");
					}}
					disabled={!transaction}
				>
					Recheck AML
				</Button>
			</DrawerFooter>

			<DecisionDialog
				type="approve"
				open={activeDialog === "approve"}
				onOpenChange={(open) => {
					if (!open) closeDialog();
				}}
				reason={decisionReason}
				onReasonChange={setDecisionReason}
				totpCode={totpCode}
				onTotpCodeChange={setTotpCode}
				isSubmitting={approveMutation.isPending}
				onConfirm={handleDecision}
				requires2FA
			/>

			<DecisionDialog
				type="reject"
				open={activeDialog === "reject"}
				onOpenChange={(open) => {
					if (!open) closeDialog();
				}}
				reason={decisionReason}
				onReasonChange={setDecisionReason}
				totpCode={totpCode}
				onTotpCodeChange={setTotpCode}
				isSubmitting={rejectMutation.isPending}
				onConfirm={handleDecision}
				requires2FA
			/>

			<DecisionDialog
				type="skip"
				open={activeDialog === "skip"}
				onOpenChange={(open) => {
					if (!open) closeDialog();
				}}
				reason={decisionReason}
				onReasonChange={setDecisionReason}
				totpCode={totpCode}
				onTotpCodeChange={setTotpCode}
				isSubmitting={skipMutation.isPending}
				onConfirm={handleDecision}
				requires2FA
			/>

			<DecisionDialog
				type="recheck"
				open={activeDialog === "recheck"}
				onOpenChange={(open) => {
					if (!open) closeDialog();
				}}
				reason={decisionReason}
				onReasonChange={setDecisionReason}
				totpCode={totpCode}
				onTotpCodeChange={setTotpCode}
				isSubmitting={recheckMutation.isPending}
				onConfirm={handleDecision}
				requires2FA={false}
			/>
		</>
	);

	return (
		<EntityDrawer
			title="AML Transaction Details"
			isLoading={isLoading}
			isNotFound={!transaction && !isLoading}
			notFoundMessage="Transaction not found"
			parentPath="/aml-transactions"
		>
			{drawerContent}
		</EntityDrawer>
	);
}

type DecisionDialogProps = {
	type: "approve" | "reject" | "skip" | "recheck";
	open: boolean;
	onOpenChange: (open: boolean) => void;
	reason: string;
	onReasonChange: (value: string) => void;
	totpCode: string;
	onTotpCodeChange: (value: string) => void;
	isSubmitting: boolean;
	onConfirm: () => void;
	requires2FA: boolean;
};

const DecisionDialog = ({
	type,
	open,
	onOpenChange,
	reason,
	onReasonChange,
	totpCode,
	onTotpCodeChange,
	isSubmitting,
	onConfirm,
	requires2FA,
}: DecisionDialogProps) => {
	const isValid = requires2FA
		? reason.trim().length >= 10 && totpCode.trim().length === 6
		: reason.trim().length >= 10;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{type === "approve" && (
							<CheckCircle2 className="h-5 w-5 text-green-600" />
						)}
						{type === "reject" && (
							<XCircle className="h-5 w-5 text-destructive" />
						)}
						{type === "skip" && (
							<AlertTriangle className="h-5 w-5 text-orange-600" />
						)}
						{type === "recheck" && (
							<AlertTriangle className="h-5 w-5 text-yellow-600" />
						)}
						{type === "approve"
							? "Approve Transaction"
							: type === "reject"
								? "Reject Transaction"
								: type === "skip"
									? "Skip AML Check"
									: "Recheck AML"}
					</DialogTitle>
					<DialogDescription>
						{type === "approve"
							? "This will approve the transaction and allow it to proceed. This action will be logged."
							: type === "reject"
								? "This will permanently reject the transaction. This action cannot be undone."
								: type === "skip"
									? "This will skip the AML check and allow the transaction to proceed. Use only when AML API errors occur."
									: "This will trigger a new AML check for the transaction."}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="reason">
							Reason <span className="text-destructive">*</span>
						</Label>
						<Textarea
							id="reason"
							placeholder="Enter the reason for this action (min 10 characters)"
							value={reason}
							onChange={(e) => onReasonChange(e.target.value)}
							disabled={isSubmitting}
							className="min-h-[100px] resize-none"
							maxLength={500}
						/>
						<p className="text-xs text-muted-foreground">
							{reason.length}/500 characters
						</p>
					</div>

					{requires2FA && (
						<div className="space-y-2">
							<Label htmlFor="totp">
								2FA Code <span className="text-destructive">*</span>
							</Label>
							<Input
								id="totp"
								type="text"
								placeholder="Enter your 6-digit 2FA code"
								value={totpCode}
								onChange={(e) =>
									onTotpCodeChange(e.target.value.replace(/\D/g, ""))
								}
								disabled={isSubmitting}
								maxLength={6}
								pattern="[0-9]{6}"
								inputMode="numeric"
								autoComplete="one-time-code"
							/>
							<p className="text-xs text-muted-foreground">
								Enter the 6-digit code from your authenticator app
							</p>
						</div>
					)}

					<div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
						<p className="text-sm text-yellow-800 dark:text-yellow-200">
							<strong>Note:</strong> This action will be logged in the audit
							trail for compliance purposes.
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					<Button
						onClick={onConfirm}
						disabled={isSubmitting || !isValid}
						variant={type === "reject" ? "destructive" : "default"}
					>
						{isSubmitting ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Processing...
							</>
						) : type === "approve" ? (
							<>
								<CheckCircle2 className="mr-2 h-4 w-4" />
								Approve
							</>
						) : type === "reject" ? (
							<>
								<XCircle className="mr-2 h-4 w-4" />
								Reject
							</>
						) : type === "skip" ? (
							<>
								<AlertTriangle className="mr-2 h-4 w-4" />
								Skip AML
							</>
						) : (
							<>
								<AlertTriangle className="mr-2 h-4 w-4" />
								Recheck AML
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
